import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { GridRequester } from '../../requesters/grid/grid.requester';
import {
	GridAction,
	GridActionPopupComponent,
	IGridActionModalData,
} from '../components/grid-action-popup/grid-action-popup.component';
import { GridAdapter } from '../grid.adapter';
import {
	bulkDeleteGrids,
	deleteGrid,
	deleteGridFail,
	deleteGridSuccess,
	duplicateGrid,
	duplicateGridFail,
	duplicateGridSuccess,
	fetchGridListFail,
	fetchGridListSuccess,
	initGridList,
	navigateToGridCreate,
	navigateToGridEdit,
	openDeleteConfirmationPopup,
	refreshGridList,
	toggleGridStatus,
} from './grid.actions';

@Injectable()
export class GridEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToGridCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToGridCreate),
				tap(() => this.stateService.go(states.GridSelect))
			),
		{
			dispatch: false,
		}
	);

	public navigateToGridEdit = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToGridEdit),
				tap((a) =>
					a.goToVisuTab
						? this.stateService.go(states.GridVisualization, {
								type: `${a.gridType}`,
								id: `${a.id}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.GridEdit, {
								type: `${a.gridType}`,
								id: `${a.id}`,
						  })
				)
			),
		{
			dispatch: false,
		}
	);
	// ##########################################################
	// Business Effects
	// ##########################################################

	public initGridList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initGridList),
			switchMap(() => this.gridRequester.getGridList()),
			map((response) =>
				fetchGridListSuccess({
					gridList: this.gridAdapter.generateGridList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchGridListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshGridList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshGridList),
			switchMap(() => this.gridRequester.getGridList(false)),
			map((response) =>
				fetchGridListSuccess({
					gridList: this.gridAdapter.generateGridList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchGridListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleGridStatus),
			switchMap((action) =>
				this.gridRequester.getGrid(action.gridId, action.gridType)
			),
			switchMap((grid) =>
				this.gridRequester.changeActivationStatusGrid({
					...grid,
					metadata: {
						...grid.metadata,
						actif: !grid.metadata.actif,
					},
				})
			),
			map(() => refreshGridList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshGridList());
				return caught;
			})
		)
	);

	public deleteGrid$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteGrid),
			switchMap((action) =>
				this.gridRequester.deleteGrid(action.gridId, action.gridType)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@grid.delete.success:`
				);
				this.store.dispatch(deleteGridSuccess());
				return refreshGridList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError($localize`:i18n=@@grid.delete.error:`);
				this.store.dispatch(
					deleteGridFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteGrids$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteGrids),
			switchMap((action) => this.gridRequester.deleteManyGrids(action.gridIds)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@grid.delete.multiple.success:`
				);
				this.store.dispatch(deleteGridSuccess());
				return refreshGridList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@grid.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteGridFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public duplicateGrid$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateGrid),
			switchMap((action) =>
				combineLatest([
					of(action),
					this.gridRequester.duplicateGrid(action.gridId, action.gridType),
				])
			),
			tap(([action, id]) => {
				this.store.dispatch(
					duplicateGridSuccess({
						id,
						gridType: action.gridType,
					})
				);
			}),
			map(() => refreshGridList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(duplicateGridFail(error));
				return caught;
			})
		)
	);

	public navigateToGridEditAfterDuplicate$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateGridSuccess),
			map((action) =>
				navigateToGridEdit({
					id: action.id,
					gridType: action.gridType,
					goToVisuTab: false,
				})
			)
		)
	);

	public deleteFailAction$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(deleteGridFail),
				tap(({ error }) => {
					const httpErrorsResponse = error as HttpErrorResponse;
					// eslint-disable-next-line camelcase
					const { delete_errors_report } = httpErrorsResponse.error;

					// eslint-disable-next-line camelcase
					if (
						delete_errors_report.total_deleted_element &&
						delete_errors_report.total_deleted_element !== 0
					) {
						this.store.dispatch(refreshGridList());
					}
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IGridActionModalData>(
					GridActionPopupComponent,
					{
						title: $localize`:i18n=@@grid.action.popup.delete.header:`,
						subTitle: '',
						gridId: action.gridId,
						element: action.gridLabel,
						action: GridAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.gridId.length === 1) {
					return deleteGrid({
						gridId: action.gridId[0],
						gridType: action.gridType[0],
					});
				} else {
					return bulkDeleteGrids({
						gridIds: action.gridId,
					});
				}
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly gridRequester: GridRequester,
		private readonly gridAdapter: GridAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
